import { lireTout, ecrire, supprimer, neuf, touche } from './base.js';
import { PROFILS_EXEMPLE } from '../profils.js';

/**
 * Accès aux profils. Toute écriture passe par ici : `updatedAt` doit être posé
 * systématiquement, sous peine de perdre la modification à la synchro (AD-7).
 */

export async function listerProfils() {
  const liste = await lireTout('profiles');
  return liste.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

/**
 * Premier lancement : on pré-remplit avec des profils d'exemple plutôt que de
 * présenter une liste vide. L'utilisateur ajuste, il ne part pas de rien.
 *
 * Deux protections, contre le même défaut sous deux formes.
 *
 * Les identifiants des modèles sont fixes (voir `PROFILS_EXEMPLE`), donc
 * amorcer deux fois réécrit les mêmes profils au lieu d'en créer d'autres.
 * C'est la protection de fond, celle qui vaut aussi entre deux appareils.
 *
 * Le verrou ci-dessous traite la forme concurrente : deux appels lancés avant
 * que le premier n'ait écrit voient tous deux une base vide. Ils partagent
 * désormais une seule exécution. React.StrictMode le déclenche à chaque
 * démarrage en développement - c'est ainsi que le défaut s'est manifesté.
 */
let amorcage = null;

export async function amorcerSiVide() {
  if (amorcage) return amorcage;
  amorcage = (async () => {
    const existants = await listerProfils();
    if (existants.length) return existants;
    for (const modele of PROFILS_EXEMPLE) await ecrire('profiles', neuf(modele));
    return listerProfils();
  })();
  try {
    return await amorcage;
  } finally {
    // Libéré après coup : un échec ne doit pas condamner l'amorçage pour
    // toute la session.
    amorcage = null;
  }
}

export async function enregistrerProfil(profil) {
  const complet = profil.id ? touche(profil) : neuf(profil);
  await ecrire('profiles', complet);
  // Un seul profil par défaut : poser le drapeau ici plutôt que dans
  // l'interface évite deux défauts concurrents selon le chemin emprunté.
  if (complet.isDefault) await retirerDefautSauf(complet.id);
  return complet;
}

async function retirerDefautSauf(id) {
  for (const p of await listerProfils()) {
    if (p.id !== id && p.isDefault) await ecrire('profiles', touche({ ...p, isDefault: false }));
  }
}

export async function definirDefaut(id) {
  const liste = await listerProfils();
  for (const p of liste) {
    const attendu = p.id === id;
    if (p.isDefault !== attendu) await ecrire('profiles', touche({ ...p, isDefault: attendu }));
  }
  return listerProfils();
}

export async function dupliquerProfil(profil) {
  const { id, createdAt, updatedAt, ...reste } = profil;
  const liste = await listerProfils();
  return enregistrerProfil({
    ...reste,
    name: `${profil.name} (copie)`,
    isDefault: false,
    order: liste.length,
  });
}

export async function supprimerProfil(id) {
  const efface = await supprimer('profiles', id);
  // Supprimer le profil par défaut laisserait l'écran Coter sans sélection : on
  // en promeut un autre plutôt que de laisser l'application dans cet état.
  const restants = await listerProfils();
  if (efface?.isDefault && restants.length) await definirDefaut(restants[0].id);
  return restants;
}

export async function deplacerProfil(id, sens) {
  const liste = await listerProfils();
  const i = liste.findIndex((p) => p.id === id);
  const j = i + sens;
  if (i < 0 || j < 0 || j >= liste.length) return liste;
  [liste[i], liste[j]] = [liste[j], liste[i]];
  // L'ordre est réécrit sur toute la liste : des index non contigus
  // finiraient par produire des égalités et un tri instable.
  for (const [rang, p] of liste.entries()) {
    if (p.order !== rang) await ecrire('profiles', touche({ ...p, order: rang }));
  }
  return listerProfils();
}
